/**
 * One company's books, decided from what the server says: whether a page
 * opens before the books are set up, the set-up rules, and the dates a page
 * defaults to. Pure, so the decisions are testable.
 */
import type { components } from "@linkhr/api-client/core";
import { dateToIso, isoToDate } from "@linkhr/i18n/format";

type CapabilityCompany = components["schemas"]["CapabilityCompany"];
export type LedgerWindow = components["schemas"]["CapabilityLedgerWindow"];

/** The permission codes the screens check, as the operations name them. */
export const CODES = {
  settings: "accounting:settings:manage",
  report: "accounting:report:read",
  journal: "accounting:journal:post",
  reverse: "accounting:journal:reverse",
  sales: "accounting:sales:read",
  salesDraft: "accounting:sales:draft",
  purchasesRead: "accounting:purchases:read",
  purchasesApprove: "accounting:purchases:approve",
  documentRead: "accounting:document:read",
  documentDraft: "accounting:document:draft",
  documentApprove: "accounting:document:approve",
  evidence: "accounting:evidence:read",
  audit: "accounting:audit:read",
  bankImport: "accounting:bank:import",
  bankReconcile: "accounting:bank:reconcile",
  bankDetailsPropose: "accounting:bank-details:propose",
  bankDetailsApprove: "accounting:bank-details:approve",
  softClose: "accounting:period:soft-close",
  exception: "accounting:period:soft-exception",
  paymentPrepare: "accounting:payment:prepare",
  paymentApprove: "accounting:payment:approve",
  paymentRelease: "accounting:payment:release",
  contactManage: "accounting:contact:manage",
  /** Every accounting file export (a CSV); the owner holds it. */
  export: "accounting:export",
} as const;

/** The codes this person holds in the company; the account owner holds every one. */
export const codesOf = (company: CapabilityCompany): readonly string[] =>
  company.grants.ACCOUNTING?.permissions ?? [];

/** Who may read each side of open items: any one of its codes. */
export const OPEN_ITEM_CODES = {
  RECEIVABLE: [CODES.sales, CODES.documentRead, CODES.report],
  PAYABLE: [CODES.purchasesRead, CODES.documentRead, CODES.report],
} as const;

const PURCHASES = [CODES.purchasesRead, CODES.documentRead] as const;
const DRAFT = [CODES.documentDraft] as const;
const APPROVE = [CODES.purchasesApprove, CODES.documentApprove] as const;
const PAY = [CODES.paymentPrepare, CODES.paymentApprove, CODES.paymentRelease] as const;
const BANK = [
  CODES.bankImport,
  CODES.bankReconcile,
  CODES.report,
  CODES.settings,
  CODES.journal,
  CODES.audit,
  ...PAY,
] as const;

/** The server's code sets for supplier documents, suppliers, banking and the queue: any one code of a set opens it. */
export const CODE_SETS = {
  /** Any read: the review queue, settings, members, tax codes, a supplier by id. */
  R: [
    ...PURCHASES,
    CODES.sales,
    CODES.report,
    CODES.evidence,
    CODES.audit,
    ...DRAFT,
    CODES.salesDraft,
    CODES.journal,
    ...APPROVE,
    CODES.bankDetailsPropose,
    CODES.bankDetailsApprove,
    CODES.settings,
    ...PAY,
    CODES.bankImport,
    CODES.bankReconcile,
    CODES.contactManage,
  ],
  /** Supplier bills, credits and suppliers. */
  P: PURCHASES,
  /** Any supplier payment code: live payment figures and candidates. */
  PAY,
  /** Supplier payments read: a purchases read or any payment code. */
  PR: [...PURCHASES, ...PAY],
  /** Drafting bills. */
  D: DRAFT,
  /** Approving and the other review and posted-bill actions. */
  A: APPROVE,
  /** Drafting or approving: queue actions, voiding. */
  DA: [...DRAFT, ...APPROVE],
  /** Bank, wallet and cash accounts and their balances. */
  BANK,
  /** Bank movements read (and their queue items): a bank read or drafting. */
  MOVE: [...BANK, CODES.documentDraft],
  /** Preparing a bank movement: create, save, preview, send, void, attach a file. */
  PREP: [CODES.documentDraft, CODES.journal, CODES.bankReconcile],
  /** A supplier payment's files: any payment code or the evidence read. */
  PAY_FILE: [...PAY, CODES.evidence],
  /** A bank movement's files. */
  MOVE_FILE: [CODES.documentDraft, CODES.journal, CODES.bankReconcile, CODES.evidence],
  /** Adding, changing and confirming suppliers. */
  CONTACT: [CODES.contactManage],
  /** A new supplier from a bill: the supplier code, or approving the bill. */
  CONTACT_FROM_DOC: [CODES.contactManage, ...APPROVE],
} as const;

export type BooksGate = "open" | "setup" | "not_ready" | "home";

/**
 * What a page under `/c/$companyId/` opens to, by its path after that prefix
 * ("" is home). Users and roles never needs the books; set-up is for someone
 * who may set them up, and only while they aren't; every other page waits for
 * them.
 */
export function booksGate(booksReady: boolean, canSetUp: boolean, path: string): BooksGate {
  if (path === "settings/users") return "open";
  if (path === "setup") return booksReady ? "home" : canSetUp ? "open" : "not_ready";
  if (booksReady) return "open";
  return canSetUp ? "setup" : "not_ready";
}

const pad = (n: number): string => String(n).padStart(2, "0");

/** The first day of the fiscal year `today` falls in, for a year that starts in `startMonth` (1–12). */
export function fiscalYearStart(startMonth: number, today: string): string {
  const year = Number(today.slice(0, 4));
  const month = Number(today.slice(5, 7));
  return `${month >= startMonth ? year : year - 1}-${pad(startMonth)}-01`;
}

export type SetupProblem = "first_of_month" | "future" | "before_year";

/**
 * Why `booksStartOn` can't start the books, or null: it must be the first day
 * of a month of the current fiscal year, and not after today.
 */
export function setupProblem(
  startMonth: number,
  booksStartOn: string,
  today: string,
): SetupProblem | null {
  if (!/^\d{4}-(0[1-9]|1[0-2])-01$/.test(booksStartOn)) return "first_of_month";
  if (booksStartOn > today) return "future";
  if (booksStartOn < fiscalYearStart(startMonth, today)) return "before_year";
  return null;
}

/** The last day of `month` (`YYYY-MM`). */
function lastDayOf(month: string): string {
  const end = new Date(Date.UTC(Number(month.slice(0, 4)), Number(month.slice(5, 7)), 0));
  return end.toISOString().slice(0, 10);
}

/** The month `n` months before `month` (`YYYY-MM`). */
export function monthsBefore(month: string, n: number): string {
  const start = new Date(Date.UTC(Number(month.slice(0, 4)), Number(month.slice(5, 7)) - 1 - n, 1));
  return start.toISOString().slice(0, 7);
}

/** `iso` moved by `days` calendar days. */
export function addDays(iso: string, days: number): string {
  const date = isoToDate(iso) ?? new Date();
  date.setDate(date.getDate() + days);
  return dateToIso(date);
}

/**
 * The days a lock exception can run to, from today: it ends with the chosen
 * day, and the last one (the default) is the day before the day seven days
 * from now falls on, in company time.
 */
export const exceptionDays = (today: string): string[] =>
  Array.from({ length: 7 }, (_, i) => addDays(today, i));

/**
 * The day a ledger read defaults to: the company's today, or for a reader
 * limited to months the last day of their last month when that comes first.
 */
export function ledgerAsOf(today: string, window: LedgerWindow | null): string {
  if (window === null) return today;
  const last = lastDayOf(window.to);
  return last < today ? last : today;
}

export interface HomeDates {
  asOf: string;
  /** The first day of `asOf`'s month (or of the reader's months, when later). */
  monthFrom: string;
  /** Twelve months of cash, ending with `asOf`'s month. */
  cashFrom: string;
}

/** The home's ranges, kept inside a window-limited reader's months. */
export function homeDates(today: string, window: LedgerWindow | null): HomeDates {
  const asOf = ledgerAsOf(today, window);
  const month = asOf.slice(0, 7);
  const floor = window === null ? "" : `${window.from}-01`;
  const later = (date: string): string => (date > floor ? date : floor);
  return {
    asOf,
    monthFrom: later(`${month}-01`),
    cashFrom: later(`${monthsBefore(month, 11)}-01`),
  };
}
